import cn from "classnames";
import { Field, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import iPaperPlane from "../../../assets/icons/paper-plane.svg";
import iCheck from "../../../assets/icons/check.svg";
import privacyPolicyPdf from "../../../assets/files/footer-form/policy-privacy.pdf";
import { selfEmployedRegistrationValidationSchema } from "../../../const/selfEmployedRegistrationValidationSchema";
import { postRegistrationApi } from "../../../api/api";
import ButtonFile from "../../common/button-file/button-file";
import Title from "../../common/title/title";
import { TRegistrationForm } from "../../../types/types";
import "./self-employed-page-form.sass";

const REGISTRATION_LINK_KEYS = new Set([
  "passportFirstPageLink",
  "passportRegistrationPageLink",
  "parentalConsentLink",
]);

type FileFieldName =
  | "passportFirstPage"
  | "passportAddressPage"
  | "passportRegistrationPage"
  | "parentsAgreeFile";

type FileLinkFieldName =
  | "passportFirstPageLink"
  | "passportRegistrationPageLink"
  | "parentalConsentLink";

function buildRegistrationFormData(values: TRegistrationForm) {
  const formData = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value instanceof File) {
      formData.append(key, value);
      return;
    }
    if (typeof value === "string" && !REGISTRATION_LINK_KEYS.has(key)) {
      formData.append(key, value);
      return;
    }
    if (typeof value === "boolean") {
      formData.append(key, String(value));
    }
  });

  return formData;
}

function applyFileFieldValue(
  field: FileFieldName,
  linkField: FileLinkFieldName,
  event: React.ChangeEvent<HTMLInputElement>,
  setFieldValue: FormikProps<TRegistrationForm>["setFieldValue"],
  setFieldTouched: FormikProps<TRegistrationForm>["setFieldTouched"],
) {
  const file = event.currentTarget.files?.[0];
  if (!file) {
    return;
  }
  setFieldValue(field, file);
  setFieldValue(linkField, file.name);
  setFieldTouched(field, true);
}

const SelfEmployedFormPageForm = () => {
  const passportFirstRef = useRef<HTMLInputElement>(null);
  const passportRegistrationRef = useRef<HTMLInputElement>(null);
  const parentalConsentRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submitControllerRef = useRef<AbortController | null>(null);

  const isAbortError = (error: unknown) =>
    error instanceof DOMException && error.name === "AbortError";

  useEffect(() => {
    return () => {
      submitControllerRef.current?.abort();
    };
  }, []);

  const onSendForm = (values: TRegistrationForm) => {
    if (isSubmitting || isSubmitted) {
      return;
    }

    const formData = buildRegistrationFormData(values);
    setSubmitting(true);
    setSubmitError("");
    submitControllerRef.current?.abort();
    const controller = new AbortController();
    submitControllerRef.current = controller;

    postRegistrationApi(formData, { signal: controller.signal })
      .then(() => {
        if (controller.signal.aborted) {
          return;
        }
        setSubmitted(true);
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        setSubmitError("Не удалось отправить заявку. Попробуйте еще раз.");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSubmitting(false);
        }
      });
  };

  const handleFileChange = (
    field: FileFieldName,
    linkField: FileLinkFieldName,
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikProps<TRegistrationForm>["setFieldValue"],
    setFieldTouched: FormikProps<TRegistrationForm>["setFieldTouched"],
  ) => {
    applyFileFieldValue(field, linkField, event, setFieldValue, setFieldTouched);
  };

  const handlePassportFirstClick = () => passportFirstRef.current?.click();
  const handlePassportRegistrationClick = () =>
    passportRegistrationRef.current?.click();
  const handleParentalConsentClick = () => parentalConsentRef.current?.click();

  return (
    <div className="self-employed-page-form">
      <Title>Регистрация</Title>

      <div className="self-employed-page-form__subtitle">
        Заполните форму заявки, прикрепите файлы и нажмите Отправить заявку.
      </div>

      <Formik<TRegistrationForm>
        initialValues={{
          fullName: "",
          birthDate: "",
          passportNumber: "",
          passportIssuedBy: "",
          passportDivisionCode: "",
          registrationAddress: "",
          phone: "",
          email: "",
          inn: "",
          bankDetails: "",
          bankBik: "",
          passportFirstPage: null,
          passportAddressPage: null,
          parentsAgreeFile: null,
          passportFirstPageLink: "",
          passportRegistrationPageLink: "",
          parentalConsentLink: "",
          consent: false,
        }}
        onSubmit={onSendForm}
        validationSchema={selfEmployedRegistrationValidationSchema}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          handleSubmit,
        }) => (
          <form
            className="self-employed-page-form__form"
            onSubmit={handleSubmit}
          >
            <div className="self-employed-page-form__form-block">
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="fullName"
                  className={cn("input input_width50", {
                    error: errors.fullName && touched.fullName,
                  })}
                  placeholder="ФИО *"
                />
                {errors.fullName && touched.fullName && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.fullName}
                  </div>
                )}
                {!(errors.fullName && touched.fullName) && (
                  <div className="self-employed-page-form__hint">
                    (полностью)
                  </div>
                )}
              </div>
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="birthDate"
                  className={cn("input input_width50", {
                    error: errors.birthDate && touched.birthDate,
                  })}
                  placeholder="Дата рождения * (DD.MM.YYYY)"
                />
                {errors.birthDate && touched.birthDate && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.birthDate}
                  </div>
                )}
              </div>
            </div>

            <div className="self-employed-page-form__form-block">
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="passportNumber"
                  className={cn("input input_width50", {
                    error: errors.passportNumber && touched.passportNumber,
                  })}
                  placeholder="Серия и номер паспорта *"
                />
                {errors.passportNumber && touched.passportNumber && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.passportNumber}
                  </div>
                )}
              </div>
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="registrationAddress"
                  className={cn("input input_width50", {
                    error:
                      errors.registrationAddress && touched.registrationAddress,
                  })}
                  placeholder="Адрес регистрации *"
                />
                {errors.registrationAddress && touched.registrationAddress && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.registrationAddress}
                  </div>
                )}
                {!(
                  errors.registrationAddress && touched.registrationAddress
                ) && (
                  <div className="self-employed-page-form__hint">
                    (в точности как в паспорте – город, район, улица, дом, кв. и
                    т.д.)
                  </div>
                )}
              </div>
            </div>

            <div className="self-employed-page-form__form-block">
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="passportIssuedBy"
                  className={cn("input input_width50", {
                    error: errors.passportIssuedBy && touched.passportIssuedBy,
                  })}
                  placeholder="Кем и когда выдан паспорт *"
                />
                {errors.passportIssuedBy && touched.passportIssuedBy && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.passportIssuedBy}
                  </div>
                )}
              </div>
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="passportDivisionCode"
                  className={cn("input input_width50", {
                    error:
                      errors.passportDivisionCode &&
                      touched.passportDivisionCode,
                  })}
                  placeholder="Код подразделения *"
                />
                {errors.passportDivisionCode &&
                  touched.passportDivisionCode && (
                    <div className="error self-employed-page-form__error-inline">
                      {errors.passportDivisionCode}
                    </div>
                  )}
              </div>
            </div>

            <div className="self-employed-page-form__form-block">
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="phone"
                  className={cn("input input_width50", {
                    error: errors.phone && touched.phone,
                  })}
                  placeholder="Номер телефона *"
                />
                {errors.phone && touched.phone && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.phone}
                  </div>
                )}
              </div>
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="email"
                  name="email"
                  className={cn("input input_width50", {
                    error: errors.email && touched.email,
                  })}
                  placeholder="Email *"
                />
                {errors.email && touched.email && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.email}
                  </div>
                )}
              </div>
            </div>

            <div className="self-employed-page-form__form-block">
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="inn"
                  className={cn("input input_width50", {
                    error: errors.inn && touched.inn,
                  })}
                  placeholder="ИНН *"
                />
                {errors.inn && touched.inn && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.inn}
                  </div>
                )}
                {!(errors.inn && touched.inn) && (
                  <div className="self-employed-page-form__hint">
                    (физ. лица, 12 цифр)
                  </div>
                )}
              </div>

              <div className="self-employed-page-form__field-wrap">
                <div className="file-input file-input_width50">
                  <Field
                    type="text"
                    name="passportFirstPageLink"
                    className={cn("input", {
                      error:
                        errors.passportFirstPage && touched.passportFirstPage,
                    })}
                    placeholder="Первая страница паспорта *"
                    readOnly
                  />

                  <ButtonFile onClick={handlePassportFirstClick} />

                  <input
                    ref={passportFirstRef}
                    type="file"
                    accept="image/*,.pdf"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileChange(
                        "passportFirstPage",
                        "passportFirstPageLink",
                        e,
                        setFieldValue,
                        setFieldTouched,
                      )
                    }
                  />
                </div>
                {!(
                  errors.passportFirstPage && touched.passportFirstPage
                ) && (
                  <div className="self-employed-page-form__hint">
                    Загрузите изображение в формате jpg, jpeg, png, pdf
                  </div>
                )}
                {errors.passportFirstPage && touched.passportFirstPage && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.passportFirstPage}
                  </div>
                )}
              </div>
            </div>
            {values.passportFirstPage && (
              <div className="self-employed-page-form__file-info">
                Загружен файл: {values.passportFirstPage.name}
              </div>
            )}

            <div className="self-employed-page-form__form-block">
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="bankDetails"
                  className={cn("input input_width50", {
                    error: errors.bankDetails && touched.bankDetails,
                  })}
                  placeholder="Банковские реквизиты *"
                />
                {errors.bankDetails && touched.bankDetails && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.bankDetails}
                  </div>
                )}
                {!(errors.bankDetails && touched.bankDetails) && (
                  <div className="self-employed-page-form__hint">
                    (номер расчетного счета, 20 цифр, начинается с 40817)
                  </div>
                )}
              </div>

              <div className="self-employed-page-form__field-wrap">
                <div className="file-input file-input_width50">
                  <Field
                    type="text"
                    name="passportRegistrationPageLink"
                    className={cn("input", {
                      error:
                        errors.passportAddressPage &&
                        touched.passportAddressPage,
                    })}
                    placeholder="Страница паспорта с пропиской *"
                    readOnly
                  />
                  <ButtonFile onClick={handlePassportRegistrationClick} />
                  <input
                    ref={passportRegistrationRef}
                    type="file"
                    accept="image/*,.pdf"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileChange(
                        "passportAddressPage",
                        "passportRegistrationPageLink",
                        e,
                        setFieldValue,
                        setFieldTouched,
                      )
                    }
                  />
                </div>
                {!(
                  errors.passportAddressPage && touched.passportAddressPage
                ) && (
                  <div className="self-employed-page-form__hint">
                    Загрузите изображение в формате jpg, jpeg, png, pdf
                  </div>
                )}
                {errors.passportAddressPage && touched.passportAddressPage && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.passportAddressPage}
                  </div>
                )}
              </div>
            </div>
            {values.passportAddressPage && (
              <div className="self-employed-page-form__file-info">
                Загружен файл: {values.passportAddressPage.name}
              </div>
            )}

            <div className="self-employed-page-form__form-block">
              <div className="self-employed-page-form__field-wrap">
                <Field
                  type="text"
                  name="bankBik"
                  className={cn("input input_width50", {
                    error: errors.bankBik && touched.bankBik,
                  })}
                  placeholder="БИК банка *"
                />
                {errors.bankBik && touched.bankBik && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.bankBik}
                  </div>
                )}
                {!(errors.bankBik && touched.bankBik) && (
                  <div className="self-employed-page-form__hint">
                    БИК – 9 цифр
                  </div>
                )}
              </div>

              <div className="self-employed-page-form__field-wrap">
                <div className="file-input file-input_width50">
                  <Field
                    type="text"
                    name="parentalConsentLink"
                    className={cn("input", {
                      error:
                        errors.parentsAgreeFile && touched.parentsAgreeFile,
                    })}
                    placeholder="Разрешение от родителей (при необходимости)"
                    readOnly
                  />
                  <ButtonFile onClick={handleParentalConsentClick} />
                  <input
                    ref={parentalConsentRef}
                    type="file"
                    accept="image/*,.pdf"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileChange(
                        "parentsAgreeFile",
                        "parentalConsentLink",
                        e,
                        setFieldValue,
                        setFieldTouched,
                      )
                    }
                  />
                </div>
                {!(
                  errors.parentsAgreeFile && touched.parentsAgreeFile
                ) && (
                  <div className="self-employed-page-form__hint">
                    Загрузите файл в формате docx или pdf
                  </div>
                )}
                {errors.parentsAgreeFile && touched.parentsAgreeFile && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.parentsAgreeFile}
                  </div>
                )}
              </div>
            </div>
            {values.parentsAgreeFile && (
              <div className="self-employed-page-form__file-info">
                Загружен файл: {values.parentsAgreeFile.name}
              </div>
            )}

            <div className="self-employed-page-form__button-block">
              <div className="self-employed-page-form__text">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <Field
                    type="checkbox"
                    name="consent"
                    style={{
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  />
                  <span>
                    Я согласен с{" "}
                    <a
                      href={privacyPolicyPdf}
                      target="_blank"
                      rel="noreferrer"
                      className="self-employed-page-form__policy-link"
                    >
                      условиями обработки персональных данных
                    </a>
                  </span>
                </label>
                {errors.consent && touched.consent && (
                  <div className="error self-employed-page-form__error-inline">
                    {errors.consent}
                  </div>
                )}
              </div>
              <div
                className={cn("self-employed-page-form__button", {
                  "self-employed-page-form__button_disabled": isSubmitting || isSubmitted,
                })}
                onClick={() => handleSubmit()}
              >
                {isSubmitted
                  ? "ОТПРАВЛЕНО"
                  : isSubmitting
                    ? "ОТПРАВКА..."
                    : "ОТПРАВИТЬ ЗАЯВКУ"}

                <img
                  src={isSubmitted ? iCheck : iPaperPlane}
                  className="self-employed-page-form__img"
                  alt=""
                />
              </div>
            </div>
            {submitError && (
              <div className="error self-employed-page-form__error">{submitError}</div>
            )}
          </form>
        )}
      </Formik>
    </div>
  );
};

export default SelfEmployedFormPageForm;
